import React from 'react';
import { ActivityIndicator, Platform, Pressable, StyleSheet, Text, TextInput, View } from 'react-native';
import { EDITOR_COLORS, editorText, webControlStyle } from '../../../styles/editorTheme';

export interface ManageTeamSearchValues {
  email: string;
  site: string;
}

interface ManageTeamSearchBarProps {
  values: ManageTeamSearchValues;
  onValuesChange: (values: ManageTeamSearchValues) => void;
  onSearch: () => void;
  onClear: () => void;
  onAddNewAccess: () => void;
  siteOptions: string[];
  isLoading: boolean;
}

export const ManageTeamSearchBar: React.FC<ManageTeamSearchBarProps> = ({
  values,
  onValuesChange,
  onSearch,
  onClear,
  onAddNewAccess,
  siteOptions,
  isLoading,
}) => {
  const isWeb = Platform.OS === 'web';
  const hasValues = values.email.trim().length > 0 || values.site.trim().length > 0;
  const resolvedSiteOptions = [''].concat(siteOptions);
  const webSelectStyle = { ...webControlStyle, width: '100%' } as any;

  const setField = (key: keyof ManageTeamSearchValues, value: string) => {
    onValuesChange({
      ...values,
      [key]: value,
    });
  };

  const cycleSite = () => {
    if (resolvedSiteOptions.length === 0) {
      return;
    }

    const currentIndex = resolvedSiteOptions.indexOf(values.site);
    const nextIndex = currentIndex < 0 || currentIndex === resolvedSiteOptions.length - 1 ? 0 : currentIndex + 1;
    setField('site', resolvedSiteOptions[nextIndex]);
  };

  return (
    <View style={styles.container}>
      <View style={styles.tableCluster}>
        <View style={styles.columnSearch}>
          <Pressable
            onPress={onSearch}
            style={[styles.searchBtn, isLoading && styles.searchBtnDisabled]}
            disabled={isLoading}
            accessibilityLabel="Search team members"
          >
            {isLoading ? (
              <ActivityIndicator size="small" color={EDITOR_COLORS.submitText} />
            ) : (
              <Text style={styles.searchBtnText}>🔍 Search</Text>
            )}
          </Pressable>
          <View style={styles.emptyCell} />
        </View>

        <View style={styles.columnLabel}>
          <Text style={styles.label}>Email</Text>
          <Text style={styles.label}>Site</Text>
        </View>

        <View style={styles.columnInput}>
          <TextInput
            style={styles.input}
            value={values.email}
            onChangeText={(value) => setField('email', value)}
            autoCapitalize="none"
            returnKeyType="search"
            onSubmitEditing={onSearch}
          />
          {isWeb ? (
            React.createElement(
              'select' as any,
              {
                value: values.site,
                onChange: (event: any) => setField('site', String(event?.target?.value ?? '')),
                style: webSelectStyle,
                'aria-label': 'Site filter',
              },
              resolvedSiteOptions.map((option) => (
                React.createElement('option' as any, { key: option || '__all__', value: option }, option || '')
              ))
            )
          ) : (
            <Pressable onPress={cycleSite} style={styles.siteBtn} accessibilityRole="button" accessibilityLabel="Site filter">
              <Text style={styles.siteBtnText} numberOfLines={1}>
                {values.site || ''}
              </Text>
              <Text style={styles.siteChevron}>▼</Text>
            </Pressable>
          )}
        </View>

        <View style={styles.columnClear}>
          <Pressable
            onPress={onClear}
            style={[styles.clearBtn, !hasValues && styles.clearBtnDisabled]}
            disabled={!hasValues || isLoading}
            accessibilityLabel="Clear team search filters"
          >
            <Text style={styles.clearBtnText}>Clear</Text>
          </Pressable>
          <View style={styles.emptyCell} />
        </View>

        <View style={styles.columnAdd}>
          <Pressable
            onPress={onAddNewAccess}
            style={styles.addAccessBtn}
            accessibilityRole="button"
            accessibilityLabel="Add new access"
          >
            <Text style={styles.addAccessBtnText}>Add New Access</Text>
          </Pressable>
          <View style={styles.emptyCell} />
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    backgroundColor: EDITOR_COLORS.background,
    borderRadius: 14,
    padding: 16,
    marginBottom: 18,
    alignItems: 'center',
  },
  tableCluster: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    width: 'auto',
    alignSelf: 'center',
  },
  columnSearch: {
    width: 120,
    gap: 4,
  },
  columnLabel: {
    minWidth: 40,
    paddingLeft: 10,
    height: 56,
    justifyContent: 'space-between',
  },
  columnInput: {
    width: 140,
    height: 56,
    justifyContent: 'space-between',
    flexShrink: 1,
  },
  columnClear: {
    width: 72,
    gap: 4,
    marginLeft: 8,
  },
  columnAdd: {
    width: 120,
    height: 56,
    justifyContent: 'space-between',
  },
  emptyCell: {
    height: 26,
  },
  label: {
    ...editorText,
    height: 26,
    lineHeight: 26,
    color: EDITOR_COLORS.textMuted,
  },
  input: {
    ...editorText,
    height: 26,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 3,
    paddingHorizontal: 7,
    color: EDITOR_COLORS.textPrimary,
    backgroundColor: EDITOR_COLORS.background,
    width: '100%',
  },
  siteBtn: {
    height: 26,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 3,
    paddingHorizontal: 7,
    backgroundColor: EDITOR_COLORS.background,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  siteBtnText: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
    flex: 1,
    marginRight: 6,
  },
  siteChevron: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
  },
  searchBtn: {
    width: 120,
    height: 28,
    borderRadius: 5,
    backgroundColor: EDITOR_COLORS.submitBackground,
    alignItems: 'center',
    justifyContent: 'center',
  },
  searchBtnDisabled: {
    backgroundColor: EDITOR_COLORS.submitBackgroundDisabled,
  },
  searchBtnText: {
    ...editorText,
    color: EDITOR_COLORS.submitText,
  },
  clearBtn: {
    width: 50,
    height: 26,
    borderRadius: 5,
    backgroundColor: EDITOR_COLORS.background,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    shadowColor: EDITOR_COLORS.placeholder,
    shadowOffset: { width: 2, height: 2 },
    shadowOpacity: 0.35,
    shadowRadius: 2,
    elevation: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  clearBtnDisabled: {
    opacity: 0.5,
  },
  clearBtnText: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
  },
  addAccessBtn: {
    width: 120,
    height: 28,
    borderRadius: 5,
    backgroundColor: EDITOR_COLORS.submitBackground,
    alignItems: 'center',
    justifyContent: 'center',
  },
  addAccessBtnText: {
    ...editorText,
    color: EDITOR_COLORS.submitText,
  },
});
